import useAxios from "../../../../../hooks/useAxios";
import useLoading from "../../../../../shared/components/loading/useLoading";
import { useEffect, useRef, useState } from "react";
import useToastWrapper from "../../../../../hooks/useToastWrapper";
import useConfirmDialog from "../../../../../shared/components/confirm-dialog/useConfirmDialog";
import {
  ADD_SECTION,
  API_PERMISSIONS,
  API_PERMISSIONS_BY_ID,
  API_ROLE_SECTION,
  DROPDOWNS_ROLES,
  DROPDOWNS_SECTIONS,
} from "../../../../../shared/lib/apiEndpointsResolver";
import { catchFunction } from "../../../../../shared/lib/utils";
import OverviewTabContent from "../../../../../shared/components/overview-panel/OverviewTabContent";
import OverviewComponent from "../../../../../shared/components/overview-panel/OverviewComponent";
import { PERMISSIONS_CONTROLCENTRE } from "../../../../../shared/lib/routesResolver";
import CCSectionsPermissionsPanel from "./CCSectionsPermissionsPanel";
import { Button } from "../../../../../shared/components/button/Button";
import AddCCSectionRoleDialog from "./add-role/AddCCSectionRoleDialog";

const CCSectionsPermissionsOverview = () => {
  const axios = useAxios();
  const { setLoading } = useLoading();
  const [permissions, setPermissions] = useState([]);
  const permissionsRef = useRef([]);
  const [roles, setRoles] = useState([]);
  const [sections, setSections] = useState([]);
  const toast = useToastWrapper();
  const deletePermissionDialog = useConfirmDialog();
  const [permissionForDeletion, setPermissionForDeletion] = useState();
  const breadcrumbItems = [
    { title: "Permisos Control Centre", link: PERMISSIONS_CONTROLCENTRE },
    { title: "Secciones" },
  ];

  const findPermissions = () => {
    setLoading(true);
    axios
      .get(`${API_PERMISSIONS}?app=control-centre-service`)
      .then((response) => {
        const data = response.data
          .filter((x) => x.key !== "role")
          .map((x) => {
            return { ...x, value: JSON.parse(x.value) };
          });
        setPermissions(data);
        permissionsRef.current = data;
      })
      .catch(catchFunction)
      .finally(() => setLoading(false));
  };

  const findRolesDropdown = () => {
    axios
      .get(DROPDOWNS_ROLES)
      .then((response) => setRoles(response.data))
      .catch(catchFunction);
  };

  const findSectionsDropdown = () => {
    axios
      .get(DROPDOWNS_SECTIONS)
      .then((response) => setSections(response.data))
      .catch(catchFunction);
  };

  const onSectionAddition = (permission, formValues) => {
    setLoading(true);
    const values = [...permission.value, formValues.section];
    const request = { sections: values };

    axios
      .put(ADD_SECTION(permission.id), request)
      .then((_) => {
        toast.success(`Sección añadida con éxito`);
        findPermissions();
      })
      .catch(catchFunction)
      .finally(() => setLoading(false));
  };

  const onSectionDeletion = (sectionId, permission) => {
    setLoading(true);
    const values = permission.value.filter((x) => x !== sectionId);
    const request = { sections: values };

    axios
      .put(ADD_SECTION(permission.id), request)
      .then((_) => {
        toast.success(`Sección eliminada con éxito`);
        findPermissions();
      })
      .catch(catchFunction)
      .finally(() => setLoading(false));
  };

  const onRoleAddition = (formValues) => {
    setLoading(true);
    axios
      .post(API_ROLE_SECTION, formValues)
      .then((response) => {
        toast.success(
          `Creado nuevo permiso para en la sección ${response.data.key} para el rol ${response.data.role.name}.`,
        );
        findPermissions();
      })
      .catch(catchFunction)
      .finally(() => setLoading(false));
  };

  const onPermissionDeletionClicked = (id) => {
    const selectedPermission = permissions.filter((x) => x.id === id)[0];
    setPermissionForDeletion(selectedPermission);
    deletePermissionDialog.display(
      `Confirmar el borrado del permiso para el rol ${selectedPermission.role.name} en la sección ${selectedPermission.key}`,
    );
  };

  const deletePermission = () => {
    setLoading(true);
    axios
      .delete(API_PERMISSIONS_BY_ID(permissionForDeletion.id))
      .then((_) => {
        toast.success(`Permiso borrado correctamente`);
        findPermissions();
      })
      .catch(catchFunction)
      .finally(() => setLoading(false));
  };

  const permissionsTabs = [
    {
      trigger: { value: "tools", label: "Tools" },
      content: (
        <OverviewTabContent
          value="tools"
          title="Permisos sección Tools"
          description="Listado completo de todos los permisos de la sección Tools agrupados por roles"
        >
          <CCSectionsPermissionsPanel
            data={permissions.filter((x) => x.key === "tools")}
            sections={sections.filter((x) => x.category === "TOOL")}
            onCreationHandler={onSectionAddition}
            onDeletionHandler={onSectionDeletion}
            onRoleDeletionHandler={onPermissionDeletionClicked}
          />
        </OverviewTabContent>
      ),
    },
    {
      trigger: { value: "dashboards", label: "Dashboards" },
      content: (
        <OverviewTabContent
          value="dashboards"
          title="Permisos sección Dashboards"
          description="Listado completo de todos los permisos de la sección Dashboards agrupados por roles"
        >
          <CCSectionsPermissionsPanel
            data={permissions.filter((x) => x.key === "dashboards")}
            sections={sections.filter((x) => x.category === "DASHBOARD")}
            onCreationHandler={onSectionAddition}
            onDeletionHandler={onSectionDeletion}
            onRoleDeletionHandler={onPermissionDeletionClicked}
          />
        </OverviewTabContent>
      ),
    },
    {
      trigger: { value: "dataExplorers", label: "Data explorers" },
      content: (
        <OverviewTabContent
          value="dataExplorers"
          title="Permisos sección Data Explorers"
          description="Listado completo de todos los permisos de la sección Data Explorers agrupados por roles"
        >
          <CCSectionsPermissionsPanel
            data={permissions.filter((x) => x.key === "data-explorers")}
            sections={sections.filter((x) => x.category === "DATA_EXPLORER")}
            onCreationHandler={onSectionAddition}
            onDeletionHandler={onSectionDeletion}
            onRoleDeletionHandler={onPermissionDeletionClicked}
          />
        </OverviewTabContent>
      ),
    },
  ];

  useEffect(() => {
    findPermissions();
    findRolesDropdown();
    findSectionsDropdown();
  }, []);

  return (
    <>
      {deletePermissionDialog.renderDialog(deletePermission)}
      <OverviewComponent
        breadcrumbItems={breadcrumbItems}
        tabs={permissionsTabs}
        options={
          <AddCCSectionRoleDialog
            onSubmitHandler={onRoleAddition}
            roles={roles}
          />
        }
        defaultTabValue="tools"
      />
    </>
  );
};

export default CCSectionsPermissionsOverview;
